// Utility functions to export chat responses to CSV or XLSX (client-side)
// - Attempts to parse markdown tables first; falls back to plain text export

import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// Simple markdown table parser
function parseMarkdownTables(markdown: string): string[][][] {
  const lines = markdown.split(/\r?\n/);
  const tables: string[][][] = [];
  let i = 0;
  while (i < lines.length) {
    // Find a potential header row with pipes
  if (lines[i].includes("|") && i + 1 < lines.length) {
      const separator = lines[i + 1].trim();
      // Markdown table separator must contain dashes and pipes, e.g., | --- | --- |
      if (/^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/.test(separator)) {
        const rows: string[][] = [];
        // Collect header and subsequent rows until a non-pipe line
        let j = i;
        while (j < lines.length && lines[j].includes("|")) {
          const cells = lines[j]
            .trim()
            // remove leading/trailing pipe
            .replace(/^\|/, "")
            .replace(/\|$/, "")
            .split("|")
            .map((c) => c.trim());
          rows.push(cells);
          j++;
        }
        // rows[0] header, rows[1] separator -> remove separator row
        if (rows.length >= 2) rows.splice(1, 1);
        if (rows.length > 0) tables.push(rows);
        i = j;
        continue;
      }
    }
    i++;
  }
  return tables;
}

function toCSV(rows: string[][]): string {
  const escape = (val: string) => {
    if (val == null) return "";
    const needsQuote = /[",\n]/.test(val);
    let v = val.replace(/"/g, '""');
    return needsQuote ? `"${v}"` : v;
  };
  return rows.map((r) => r.map(escape).join(",")).join("\n");
}

export function buildCSVFromMarkdown(markdown: string): Blob {
  const tables = parseMarkdownTables(markdown);
  if (tables.length > 0) {
    // If multiple tables, stack them separated by a blank line
    const csv = tables
      .map((t) => toCSV(t))
      .join("\n\n");
    return new Blob([csv], { type: "text/csv;charset=utf-8;" });
  }
  // Fallback: single column with the full text
  const csv = toCSV([["Response"], [markdown.replace(/\r?\n/g, " ")]]);
  return new Blob([csv], { type: "text/csv;charset=utf-8;" });
}

export function buildXLSXFromMarkdown(markdown: string): Blob {
  // Try tables first
  const tables = parseMarkdownTables(markdown);
  const wb = XLSX.utils.book_new();
  if (tables.length > 0) {
    tables.forEach((t, idx) => {
      const ws = XLSX.utils.aoa_to_sheet(t);
      XLSX.utils.book_append_sheet(wb, ws, `Table${idx + 1}`);
    });
  } else {
    const ws = XLSX.utils.aoa_to_sheet([["Response"], [markdown]]);
    XLSX.utils.book_append_sheet(wb, ws, "Response");
  }
  const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  return new Blob([wbout], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function buildPDFFromMarkdown(markdown: string): Blob {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const marginLeft = 40;
  const marginTop = 40;

  // Try to render markdown tables via autotable; fallback to plain text
  const tables = parseMarkdownTables(markdown);
  if (tables.length > 0) {
  tables.forEach((t) => {
      const head = t.length > 0 ? [t[0]] : [];
      const body = t.length > 1 ? t.slice(1) : [];
      autoTable(doc, {
        head,
        body,
    startY: (doc as any).lastAutoTable ? (doc as any).lastAutoTable.finalY + 20 : marginTop,
        styles: { fontSize: 9 },
        theme: "striped",
        headStyles: { fillColor: [0, 120, 212] },
        margin: { left: marginLeft, right: marginLeft },
        tableWidth: "auto",
      });
    });
  } else {
    // Plain text fallback with simple wrapping
    const pageWidth = doc.internal.pageSize.getWidth();
    const contentWidth = pageWidth - marginLeft * 2;
    const lines = doc.splitTextToSize(markdown, contentWidth);
    doc.text(lines, marginLeft, marginTop, { lineHeightFactor: 1.2 });
  }

  const output = doc.output("arraybuffer");
  return new Blob([output], { type: "application/pdf" });
}

export function userRequestedExport(prompt: string): { csv: boolean; xlsx: boolean; pdf: boolean } {
  const p = prompt.toLowerCase();
  const csv = /(\bcsv\b|comma[-\s]?separated|spreadsheet)/.test(p);
  const xlsx = /(\bexcel\b|\bxlsx\b|\bxls\b|spreadsheet)/.test(p);
  const pdf = /(\bpdf\b|portable document|download as pdf)/.test(p);
  return { csv, xlsx, pdf };
}
